import React, { useState, useEffect } from 'react';
import api from './api';
import useAuth from '../hooks/useAuth';
import { isSuper } from '../utils/canDo';

const WA_TYPES = [
  { group: 'KIT Campaign', types: [
    { key: 'WA_KIT_WELCOME', label: 'KIT Welcome Message', vars: ['name'], defaultMsg: 'Namaste {{name}} Ji! Welcome to our therapy program! We\'re here to help you recover and feel better soon.' },
    { key: 'WA_KIT_FOLLOWUP', label: 'KIT Follow-up Reminder', vars: ['name'], defaultMsg: 'Namaste {{name}} Ji! Just checking in — how are you feeling since our last conversation?' },
    { key: 'WA_KIT_TIP', label: 'KIT Health Tip', vars: ['name', 'text'], defaultMsg: 'Namaste {{name}} Ji! {{text}}' },
    { key: 'WA_KIT_CONSULT', label: 'KIT Consultation Reminder', vars: ['name'], defaultMsg: 'Namaste {{name}} Ji! It\'s always best to consult early. Delaying treatment allows conditions to worsen.' },
    { key: 'WA_KIT_CUSTOM', label: 'KIT Custom / Bulk Message', vars: ['name', 'messageText'], defaultMsg: 'Namaste {{name}} Ji! {{messageText}}' },
  ]},
  { group: 'Appointments', types: [
    { key: 'WA_APPT_ONLINE', label: 'Online Session Scheduled', vars: ['name', 'title', 'datetime', 'link'], defaultMsg: 'Hi {{name}}, your online session "{{title}}" is scheduled for {{datetime}}. Join here: {{link}}' },
    { key: 'WA_APPT_UPDATE', label: 'Appointment Update', vars: ['title', 'datetime', 'description', 'link'], defaultMsg: 'Update: "{{title}}" on {{datetime}}. {{description}} Join: {{link}}' },
    { key: 'WA_APPT_REMINDER', label: 'Appointment Reminder', vars: ['name', 'datetime'], defaultMsg: 'Namaste {{name}} Ji, your appointment is scheduled on {{datetime}}.' },
  ]},
  { group: 'Patient Communication', types: [
    { key: 'WA_CALL_LOG', label: 'Call Log WhatsApp', vars: ['name', 'waText'], defaultMsg: 'Namaste {{name}} Ji! {{waText}}' },
  ]},
  { group: 'Authentication', types: [
    { key: 'WA_OTP', label: 'Login OTP', vars: ['otp'], defaultMsg: 'Your MYC login OTP is *{{otp}}*. Valid for 10 minutes.' },
  ]},
];

const NOTIF_TYPES = [
  { group: 'Staff — Daily', types: [
    { key: 'MORNING_GREETING', label: 'Morning Greeting', vars: ['name', 'greeting'] },
    { key: 'ATTENDANCE_ALERT', label: 'Auto-Checkout Alert', vars: ['hours', 'mins'] },
    { key: 'GEO_EXIT_ALERT', label: 'Geo-Exit Alert', vars: ['name', 'designation', 'count', 'minutes'] },
  ]},
  { group: 'Staff — Appointments', types: [
    { key: 'APPOINTMENT_REMINDER', label: 'Tomorrow Appointments', vars: ['count', 'list'] },
    { key: 'SESSION_PENDING', label: 'Session Auto-Completed', vars: ['name'] },
    { key: 'BOOKING_ALERT', label: 'New Booking Alert', vars: ['mode', 'name', 'details'] },
  ]},
  { group: 'Staff — Tasks & Follow-ups', types: [
    { key: 'TASK_REMINDER', label: 'Task Reminder', vars: ['count', 'details'] },
    { key: 'TASK_ASSIGNED', label: 'Task Assigned', vars: ['title', 'dueDate'] },
    { key: 'DELAYED_TASK', label: 'Delayed Task', vars: ['count'] },
    { key: 'CHECKLIST_MISSED', label: 'Checklist Missed', vars: ['count', 'list'] },
  ]},
  { group: 'Staff — Saadhak Follow-ups', types: [
    { key: 'RENEWAL_REMINDER', label: 'Renewal Reminder', vars: ['count'] },
    { key: 'INACTIVE_PATIENT', label: 'Inactive Saadhak', vars: ['count'] },
    { key: 'PAYMENT_PENDING', label: 'Payment Pending', vars: ['count'] },
    { key: 'VIDEO_EXPIRY_WARNING', label: 'Video Expiry Warning', vars: ['title', 'collection', 'patientCard', 'timeLeft', 'percent'] },
  ]},
  { group: 'Saadhak (Patient-Facing)', types: [
    { key: 'PATIENT_APPT_SCHEDULED', label: 'Session Scheduled', vars: ['name', 'date', 'time'] },
    { key: 'PATIENT_APPT_TOMORROW', label: 'Session Tomorrow', vars: ['time', 'count'] },
    { key: 'PATIENT_APPT_SOON', label: 'Session Starting Soon', vars: ['timeLeft', 'time', 'minutes'] },
    { key: 'PATIENT_APPT_COMPLETED', label: 'Session Completed', vars: ['notes'] },
    { key: 'PATIENT_RENEWAL', label: 'Sessions Running Low', vars: ['remaining'] },
  ]},
  { group: 'System', types: [
    { key: 'ESCALATION', label: 'Escalation', vars: [] },
    { key: 'PLAN_APPROVAL', label: 'Plan Approval', vars: [] },
    { key: 'PLAN_DECISION', label: 'Plan Decision', vars: [] },
    { key: 'PASSWORD_CHANGED', label: 'Password Changed', vars: [] },
  ]},
];

const STYLES = `
@keyframes slideUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
.ap-card { background:#fff; border-radius:14px; box-shadow:0 2px 12px rgba(0,0,0,0.08); padding:20px 24px; margin-bottom:16px; animation:slideUp .3s ease }
.ap-section-title { font-size:13px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:12px; border-bottom:1px solid #e2e8f0; padding-bottom:6px }
.ap-row { display:flex; align-items:center; gap:12px; margin-bottom:12px; flex-wrap:wrap }
.ap-label { font-size:13px; font-weight:600; color:#334155; min-width:160px }
.ap-hint { font-size:11px; color:#94a3b8; margin-top:2px }
.ap-input { padding:6px 10px; border:1px solid #cbd5e1; border-radius:8px; font-size:13px; width:100px; transition:border-color .15s }
.ap-input:focus { outline:none; border-color:#6366f1; box-shadow:0 0 0 2px rgba(99,102,241,0.15) }
.ap-select { padding:6px 10px; border:1px solid #cbd5e1; border-radius:8px; font-size:13px; background:#fff; cursor:pointer }
.ap-toggle { width:42px; height:22px; border-radius:12px; border:none; cursor:pointer; position:relative; transition:background .2s }
.ap-toggle::after { content:''; position:absolute; top:2px; left:2px; width:18px; height:18px; border-radius:50%; background:#fff; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,0.15) }
.ap-toggle.on { background:#6366f1 }
.ap-toggle.on::after { transform:translateX(20px) }
.ap-toggle.off { background:#cbd5e1 }
.ap-save-btn { background:linear-gradient(135deg,#6366f1,#4f46e5); color:#fff; border:none; padding:10px 28px; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; transition:transform .1s, box-shadow .15s }
.ap-save-btn:active { transform:scale(0.97) }
.ap-save-btn:disabled { opacity:0.5; cursor:not-allowed }
.ap-slot-row { display:flex; align-items:center; gap:8px; margin-bottom:8px; flex-wrap:wrap }
.ap-slot-remove { background:#fee2e2; border:1px solid #fca5a5; color:#dc2626; border-radius:6px; padding:2px 8px; font-size:12px; cursor:pointer }
.ap-slot-add { background:#e0e7ff; border:1px solid #a5b4fc; color:#4338ca; border-radius:6px; padding:4px 12px; font-size:12px; cursor:pointer; font-weight:600 }
.ap-msg-row { display:flex; align-items:center; gap:8px; margin-bottom:8px }
.ap-msg-input { flex:1; padding:6px 10px; border:1px solid #cbd5e1; border-radius:8px; font-size:13px; transition:border-color .15s }
.ap-msg-input:focus { outline:none; border-color:#6366f1; box-shadow:0 0 0 2px rgba(99,102,241,0.15) }
.ap-msg-num { font-size:11px; font-weight:600; color:#94a3b8; min-width:22px; text-align:right }
.pol-tab { padding:8px 20px; border:none; border-radius:8px 8px 0 0; font-size:13px; font-weight:600; cursor:pointer; transition:background .15s, color .15s }
.pol-tab.active { background:#fff; color:#4f46e5; box-shadow:0 -2px 8px rgba(0,0,0,0.06) }
.pol-tab.inactive { background:#f1f5f9; color:#64748b }
.ntf-group-hdr { font-size:12px; font-weight:700; color:#6366f1; text-transform:uppercase; letter-spacing:0.05em; margin:18px 0 8px; padding:0 4px }
.ntf-card { background:#fff; border:1px solid #e2e8f0; border-radius:10px; padding:14px 16px; margin-bottom:10px }
.ntf-card-hdr { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px }
.ntf-type-label { font-size:13px; font-weight:700; color:#334155 }
.ntf-ttl-box { display:flex; align-items:center; gap:4px }
.ntf-ttl-input { width:60px; padding:4px 8px; border:1px solid #cbd5e1; border-radius:6px; font-size:12px; text-align:center }
.ntf-ttl-input:focus { outline:none; border-color:#6366f1 }
.ntf-tpl-label { font-size:11px; font-weight:600; color:#64748b; margin-bottom:2px }
.ntf-tpl-input { width:100%; padding:5px 8px; border:1px solid #e2e8f0; border-radius:6px; font-size:12px; font-family:inherit; resize:vertical }
.ntf-tpl-input:focus { outline:none; border-color:#6366f1; box-shadow:0 0 0 2px rgba(99,102,241,0.12) }
.ntf-vars { font-size:10px; color:#94a3b8; margin-top:2px }
.ntf-vars code { background:#f1f5f9; padding:1px 4px; border-radius:3px; font-size:10px }
`;

function minutesToTime(m) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function timeToMinutes(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

export default function Policies() {
  const { auth } = useAuth();
  const [tab, setTab] = useState('standard'); // 'standard' | 'attendance' | 'notifications'
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); 

  useEffect(() => {
    api.get('/attendance-config')
      .then(r => { setConfig(r.data); setLoading(false); })
      .catch(() => { setMsg('Failed to load config'); setLoading(false); });
  }, []);

  if (!isSuper(auth)) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Access denied. Only owner/solution can manage policies.</div>;
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading...</div>;
  if (!config) return <div style={{ padding: 40, textAlign: 'center', color: '#c00' }}>{msg || 'Config not available'}</div>;

  const setField = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const setTTL = (type, hours) => {
    setConfig(prev => ({
      ...prev,
      notificationTTL: { ...(prev.notificationTTL || {}), [type]: Math.max(0.5, parseFloat(hours) || 0) },
    }));
  };

  const setTemplate = (type, field, val) => {
    setConfig(prev => ({
      ...prev,
      notificationTemplates: {
        ...(prev.notificationTemplates || {}),
        [type]: { ...(prev.notificationTemplates || {})[type], [field]: val },
      },
    }));
  };

  const setToggle = (type, enabled) => {
    setConfig(prev => ({
      ...prev,
      notificationToggles: { ...(prev.notificationToggles || {}), [type]: enabled },
    }));
  };

  const setWaTemplate = (type, field, val) => {
    setConfig(prev => ({
      ...prev,
      whatsappTemplates: {
        ...(prev.whatsappTemplates || {}),
        [type]: { ...(prev.whatsappTemplates || {})[type], [field]: val },
      },
    }));
  };

  const setWaToggle = (type, enabled) => {
    setConfig(prev => ({
      ...prev,
      whatsappToggles: { ...(prev.whatsappToggles || {}), [type]: enabled },
    }));
  };

  const setSlot = (idx, field, val) => {
    const slots = [...(config.slots || [])];
    slots[idx] = { ...slots[idx], [field]: val };
    setConfig(prev => ({ ...prev, slots }));
  };

  const addSlot = () => {
    const slots = [...(config.slots || [])];
    const lastEnd = slots.length ? slots[slots.length - 1].end : 600;
    slots.push({ start: lastEnd, end: Math.min(lastEnd + 120, 1440) });
    setConfig(prev => ({ ...prev, slots }));
  };

  const removeSlot = (idx) => {
    const slots = [...(config.slots || [])];
    slots.splice(idx, 1);
    setConfig(prev => ({ ...prev, slots }));
  };

  const setGreeting = (idx, val) => {
    const msgs = [...(config.greetingMessages || [])];
    msgs[idx] = val;
    setConfig(prev => ({ ...prev, greetingMessages: msgs }));
  };

  const addGreeting = () => {
    const msgs = [...(config.greetingMessages || [])];
    msgs.push('');
    setConfig(prev => ({ ...prev, greetingMessages: msgs }));
  };

  const removeGreeting = (idx) => {
    const msgs = [...(config.greetingMessages || [])];
    msgs.splice(idx, 1);
    setConfig(prev => ({ ...prev, greetingMessages: msgs }));
  };

  
  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { _id, __v, createdAt, updatedAt, ...payload } = config;
      const r = await api.put('/attendance-config', payload);
      setConfig(r.data);
      setMsg('Saved successfully!');
      setToast({ type: 'success', message: 'Policies saved successfully!' });
      setTimeout(() => { setMsg(''); setToast(null); }, 3000);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to save';
      setMsg(errMsg);
      setToast({ type: 'error', message: errMsg });
      setTimeout(() => setToast(null), 4000);
    }
    setSaving(false);
  };

  return (
    <>
      <style>{STYLES}</style>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 99999,
          padding: '12px 28px', borderRadius: 10, fontSize: 14, fontWeight: 700,
          background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: toast.type === 'success' ? '#16a34a' : '#dc2626',
          border: `1.5px solid ${toast.type === 'success' ? '#86efac' : '#fca5a5'}`,
          boxShadow: '0 4px 20px rgba(0,0,0,.15)',
        }}>
          {toast.message}
        </div>
      )}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px', fontFamily: "'Segoe UI', sans-serif" }}>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>Policies</h2>
          <div style={{ color: '#888', fontSize: '0.82em', marginTop: 2 }}>Configure system-wide and attendance policies</div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 0, borderBottom: '2px solid #e2e8f0' }}>
          <button className={`pol-tab ${tab === 'standard' ? 'active' : 'inactive'}`} onClick={() => setTab('standard')}>
            Standard Policies
          </button>
          <button className={`pol-tab ${tab === 'attendance' ? 'active' : 'inactive'}`} onClick={() => setTab('attendance')}>
            Attendance
          </button>
          <button className={`pol-tab ${tab === 'notifications' ? 'active' : 'inactive'}`} onClick={() => setTab('notifications')}>
            Notifications
          </button>
          <button className={`pol-tab ${tab === 'whatsapp' ? 'active' : 'inactive'}`} onClick={() => setTab('whatsapp')}>
            WhatsApp
          </button>
        </div>

        <div style={{ background: '#fff', borderRadius: '0 0 14px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', padding: '4px 0 0' }}>

          {/* ═══ STANDARD POLICIES TAB ═══ */}
          {tab === 'standard' && (
            <div style={{ padding: '16px 0' }}>
              {/* Saadhak Login Validity */}
              <div className="ap-card">
                <div className="ap-section-title">Saadhak Login Validity</div>
                <div className="ap-row">
                  <div>
                    <span className="ap-label">Token validity</span>
                    <div className="ap-hint">How long saadhak logins stay active before they need to re-login</div>
                  </div>
                  <input type="number" className="ap-input" min={7} max={365}
                    value={config.saadhakTokenDays ?? 120}
                    onChange={e => setField('saadhakTokenDays', Math.max(7, Math.min(365, parseInt(e.target.value) || 120)))}
                  />
                  <span className="ap-hint">days</span>
                </div>
              </div>

              {/* Remote Staff Login Validity */}
              <div className="ap-card">
                <div className="ap-section-title">Remote Staff Login Validity</div>
                <div className="ap-row">
                  <div>
                    <span className="ap-label">Token validity</span>
                    <div className="ap-hint">How long owner, solution, and remote-access staff logins stay active</div>
                  </div>
                  <input type="number" className="ap-input" min={7} max={365}
                    value={config.remoteStaffTokenDays ?? 120}
                    onChange={e => setField('remoteStaffTokenDays', Math.max(7, Math.min(365, parseInt(e.target.value) || 120)))}
                  />
                  <span className="ap-hint">days</span>
                </div>
              </div>

              {/* Kiosk Session Duration */}
              <div className="ap-card">
                <div className="ap-section-title">Kiosk Session Duration</div>
                <div className="ap-row">
                  <div>
                    <span className="ap-label">Session validity</span>
                    <div className="ap-hint">How long kiosk logins (registration, expenses) stay active before re-authentication is needed</div>
                  </div>
                  <input type="number" className="ap-input" min={1} max={30}
                    value={config.kioskSessionDays ?? 5}
                    onChange={e => setField('kioskSessionDays', Math.max(1, Math.min(30, parseInt(e.target.value) || 5)))}
                  />
                  <span className="ap-hint">days</span>
                </div>
              </div>

              {/* GeoLog Tracking Slots */}
              <div className="ap-card">
                <div className="ap-section-title">GeoLog Tracking Slots</div>
                <div className="ap-hint" style={{ marginBottom: 10 }}>
                  Presence in each slot is tracked for attendance calculation. Slots are in IST.
                </div>
                {(config.slots || []).map((slot, idx) => (
                  <div key={idx} className="ap-slot-row">
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', width: 50 }}>Slot {idx + 1}</span>
                    <input type="time" className="ap-input" style={{ width: 110 }}
                      value={minutesToTime(slot.start)}
                      onChange={e => setSlot(idx, 'start', timeToMinutes(e.target.value))}
                    />
                    <span style={{ color: '#94a3b8' }}>to</span>
                    <input type="time" className="ap-input" style={{ width: 110 }}
                      value={minutesToTime(slot.end)}
                      onChange={e => setSlot(idx, 'end', timeToMinutes(e.target.value))}
                    />
                    {(config.slots || []).length > 1 && (
                      <button className="ap-slot-remove" onClick={() => removeSlot(idx)}>Remove</button>
                    )}
                  </div>
                ))}
                <button className="ap-slot-add" onClick={addSlot}>+ Add Slot</button>
              </div>

              {/* Sunday Policy */}
              <div className="ap-card">
                <div className="ap-section-title">Sunday Policy</div>
                <div className="ap-row">
                  <span className="ap-label">Sunday is treated as</span>
                  <select className="ap-select"
                    value={config.sundayPolicy || 'overtime'}
                    onChange={e => setField('sundayPolicy', e.target.value)}
                  >
                    <option value="overtime">Overtime</option>
                    <option value="halfday">Half Day</option>
                    <option value="off">Day Off (no attendance)</option>
                    <option value="normal">Normal Working Day</option>
                  </select>
                </div>
              </div>

              {/* GTP Auto-Approve Delay */}
              <div className="ap-card">
                <div className="ap-section-title">GTP Auto-Approve</div>
                <div className="ap-row">
                  <div>
                    <span className="ap-label">Auto-approve delay</span>
                    <div className="ap-hint">Treatment plans submitted by therapists are auto-approved after this delay. Set to 0 to disable (manual approval only).</div>
                  </div>
                  <input type="number" className="ap-input" min={0} max={300}
                    value={config.gtpAutoApproveDelaySec ?? 4}
                    onChange={e => setField('gtpAutoApproveDelaySec', Math.max(0, Math.min(300, parseInt(e.target.value) || 0)))}
                  />
                  <span className="ap-hint">seconds</span>
                </div>
              </div>

              {/* Greeting Messages */}
              <div className="ap-card">
                <div className="ap-section-title">Morning Greeting Messages</div>
                <div className="ap-hint" style={{ marginBottom: 10 }}>
                  One random message is sent daily at 6 AM to each staff member. Format: "Good Morning &lt;Name&gt;! &lt;message&gt; See you soon!"
                </div>
                <div style={{ maxHeight: 340, overflowY: 'auto', marginBottom: 8 }}>
                  {(config.greetingMessages || []).map((msg, idx) => (
                    <div key={idx} className="ap-msg-row">
                      <span className="ap-msg-num">{idx + 1}.</span>
                      <input type="text" className="ap-msg-input"
                        value={msg}
                        onChange={e => setGreeting(idx, e.target.value)}
                        placeholder="Enter greeting message..."
                      />
                      {(config.greetingMessages || []).length > 1 && (
                        <button className="ap-slot-remove" onClick={() => removeGreeting(idx)}>Remove</button>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="ap-slot-add" onClick={addGreeting}>+ Add Message</button>
                  <button
                    className="ap-slot-add"
                    style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    onClick={async () => {
                      try {
                        await api.post(`${process.env.REACT_APP_URL}/attendance-config/test-greeting`);
                        alert('Morning greeting sent to all staff!');
                      } catch (err) {
                        alert('Failed: ' + (err.response?.data?.message || err.message));
                      }
                    }}
                  >
                    Test Greeting Now
                  </button>
                </div>
                <div className="ap-hint" style={{ marginTop: 6 }}>
                  {(config.greetingMessages || []).length} message{(config.greetingMessages || []).length !== 1 ? 's' : ''} configured
                </div>
              </div>
            </div>
          )}

          {/* ═══ NOTIFICATIONS TAB ═══ */}
          {tab === 'notifications' && (
            <div style={{ padding: '16px 0' }}>
              <div className="ap-hint" style={{ padding: '0 4px', marginBottom: 8 }}>
                Configure how long each notification stays visible (TTL) and edit the message templates. Use {'{{variable}}'} placeholders in templates.
              </div>
              {NOTIF_TYPES.map(group => (
                <div key={group.group}>
                  <div className="ntf-group-hdr">{group.group}</div>
                  {group.types.map(nt => {
                    const ttl = (config.notificationTTL || {})[nt.key] ?? 18;
                    const tpl = (config.notificationTemplates || {})[nt.key] || {};
                    const enabled = (config.notificationToggles || {})[nt.key] !== false;
                    return (
                      <div key={nt.key} className="ntf-card" style={{ opacity: enabled ? 1 : 0.55, transition: 'opacity 0.2s' }}>
                        <div className="ntf-card-hdr">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                            <button
                              onClick={() => setToggle(nt.key, !enabled)}
                              style={{
                                width: 42, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                                background: enabled ? '#43a047' : '#ccc',
                                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                              }}
                            >
                              <span style={{
                                position: 'absolute', top: 2, left: enabled ? 22 : 2,
                                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
                              }} />
                            </button>
                            <span className="ntf-type-label">{nt.label}</span>
                          </div>
                          <div className="ntf-ttl-box">
                            <span style={{ fontSize: 11, color: '#64748b' }}>Expires in</span>
                            <input type="number" className="ntf-ttl-input" min={0.5} step={0.5}
                              value={ttl}
                              onChange={e => setTTL(nt.key, e.target.value)}
                            />
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>hrs</span>
                          </div>
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <div className="ntf-tpl-label">Title</div>
                          <input type="text" className="ntf-tpl-input"
                            value={tpl.title || ''}
                            onChange={e => setTemplate(nt.key, 'title', e.target.value)}
                            placeholder="Notification title..."
                          />
                        </div>
                        <div>
                          <div className="ntf-tpl-label">Message</div>
                          <textarea className="ntf-tpl-input" rows={2}
                            value={tpl.message || ''}
                            onChange={e => setTemplate(nt.key, 'message', e.target.value)}
                            placeholder="Notification message..."
                          />
                        </div>
                        {nt.vars.length > 0 && (
                          <div className="ntf-vars">
                            Variables: {nt.vars.map(v => <code key={v}>{`{{${v}}}`}</code>).reduce((a, b) => [a, ' ', b])}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* ═══ ATTENDANCE POLICIES TAB ═══ */}
          {tab === 'attendance' && (
            <div style={{ padding: '16px 0' }}>
              {/* Working Hours */}
              <div className="ap-card">
                <div className="ap-section-title">Working Hours</div>
                <div className="ap-row">
                  <span className="ap-label">Work Start Time</span>
                  <input type="time" className="ap-input" style={{ width: 130 }}
                    value={minutesToTime(config.workStartMinutes || 390)}
                    onChange={e => setField('workStartMinutes', timeToMinutes(e.target.value))}
                  />
                  <span className="ap-hint">IST</span>
                </div>
                <div className="ap-row">
                  <span className="ap-label">Work End Time</span>
                  <input type="time" className="ap-input" style={{ width: 130 }}
                    value={minutesToTime(config.workEndMinutes || 1200)}
                    onChange={e => setField('workEndMinutes', timeToMinutes(e.target.value))}
                  />
                  <span className="ap-hint">IST</span>
                </div>
              </div>

              {/* Geo-fencing */}
              <div className="ap-card">
                <div className="ap-section-title">Geo-fencing</div>
                <div className="ap-hint" style={{ marginBottom: 10 }}>
                  Controls access boundaries around each branch location. Applied to every API request and determines menu visibility for remote staff.
                </div>
                <div className="ap-row">
                  <div>
                    <span className="ap-label">App Geo-fence Radius</span>
                    <div className="ap-hint">Max distance from branch for full app access. Staff outside this radius with "Limited Remote Access" will only see restricted menus. Changes apply immediately.</div>
                  </div>
                  <input type="number" className="ap-input" min={10} max={1000}
                    value={config.middlewareGeoRadius ?? 80}
                    onChange={e => setField('middlewareGeoRadius', parseInt(e.target.value) || 80)}
                  />
                  <span className="ap-hint">metres</span>
                </div>
                <div className="ap-row">
                  <div>
                    <span className="ap-label">QR Check-in Radius</span>
                    <div className="ap-hint">Max distance from branch to scan attendance QR code</div>
                  </div>
                  <input type="number" className="ap-input" min={1} max={500}
                    value={config.qrGeoRadius ?? 8}
                    onChange={e => setField('qrGeoRadius', parseInt(e.target.value) || 8)}
                  />
                  <span className="ap-hint">metres</span>
                </div>
              </div>

              {/* Auto-Checkout */}
              <div className="ap-card">
                <div className="ap-section-title">Auto-Checkout</div>
                <div className="ap-row">
                  <span className="ap-label">Auto-checkout enabled</span>
                  <button
                    className={`ap-toggle ${config.autoCheckoutEnabled !== false ? 'on' : 'off'}`}
                    onClick={() => setField('autoCheckoutEnabled', config.autoCheckoutEnabled === false)}
                  />
                </div>
                {config.autoCheckoutEnabled !== false && (
                  <div className="ap-row">
                    <span className="ap-label">Auto-checkout time</span>
                    <input type="time" className="ap-input" style={{ width: 130 }}
                      value={minutesToTime(config.autoCheckoutMinutes || 1200)}
                      onChange={e => setField('autoCheckoutMinutes', timeToMinutes(e.target.value))}
                    />
                    <span className="ap-hint">IST — staff still checked in will be auto-completed</span>
                  </div>
                )}
              </div>

              {/* Auto-Leave */}
              <div className="ap-card">
                <div className="ap-section-title">Auto-Leave (End-of-Day Cron)</div>
                <div className="ap-row">
                  <span className="ap-label">Auto-leave enabled</span>
                  <button
                    className={`ap-toggle ${config.autoLeaveEnabled !== false ? 'on' : 'off'}`}
                    onClick={() => setField('autoLeaveEnabled', config.autoLeaveEnabled === false)}
                  />
                </div>
                {config.autoLeaveEnabled !== false && (
                  <div className="ap-row" style={{ marginTop: 8 }}>
                    <span className="ap-label">Cron run time</span>
                    <input type="time" className="ap-input" style={{ width: 130 }}
                      value={`${String(config.autoLeaveCronHour ?? 20).padStart(2,'0')}:${String(config.autoLeaveCronMin ?? 0).padStart(2,'0')}`}
                      onChange={e => {
                        const [h, m] = e.target.value.split(':').map(Number);
                        setField('autoLeaveCronHour', h);
                        setField('autoLeaveCronMin', m);
                      }}
                    />
                    <span className="ap-hint">IST — cron reschedules automatically on save</span>
                  </div>
                )}
                <div className="ap-hint" style={{ marginTop: 6 }}>
                  When enabled, the system creates partial/full leave records at end-of-day based on geo-presence in the tracking slots above.
                </div>
              </div>
            </div>
          )}

          {/* ═══ WHATSAPP POLICIES TAB ═══ */}
          {tab === 'whatsapp' && (
            <div style={{ padding: '16px 0' }}>
              <div className="ap-hint" style={{ padding: '0 4px', marginBottom: 8 }}>
                Manage all WhatsApp message templates across the app. Toggle them on/off and edit the message text. Use {'{{variable}}'} placeholders in templates.
              </div>
              {WA_TYPES.map(group => (
                <div key={group.group}>
                  <div className="ntf-group-hdr">{group.group}</div>
                  {group.types.map(wt => {
                    const tpl = (config.whatsappTemplates || {})[wt.key] || {};
                    const enabled = (config.whatsappToggles || {})[wt.key] !== false;
                    return (
                      <div key={wt.key} className="ntf-card" style={{ opacity: enabled ? 1 : 0.55, transition: 'opacity 0.2s' }}>
                        <div className="ntf-card-hdr">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                            <button
                              onClick={() => setWaToggle(wt.key, !enabled)}
                              style={{
                                width: 42, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                                background: enabled ? '#25d366' : '#ccc',
                                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                              }}
                            >
                              <span style={{
                                position: 'absolute', top: 2, left: enabled ? 22 : 2,
                                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
                              }} />
                            </button>
                            <span className="ntf-type-label">{wt.label}</span>
                          </div>
                        </div>
                        <div>
                          <div className="ntf-tpl-label">Message Template</div>
                          <textarea className="ntf-tpl-input" rows={3}
                            value={tpl.message ?? wt.defaultMsg}
                            onChange={e => setWaTemplate(wt.key, 'message', e.target.value)}
                            placeholder="WhatsApp message template..."
                          />
                        </div>
                        {wt.vars.length > 0 && (
                          <div className="ntf-vars">
                            Variables: {wt.vars.map(v => <code key={v}>{`{{${v}}}`}</code>).reduce((a, b) => [a, ' ', b])}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Save */}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button className="ap-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Policies'}
          </button>
          {msg && (
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: msg.includes('success') ? '#16a34a' : '#dc2626' }}>
              {msg}
            </div>
          )}
        </div>

      </div>
    </>
  );
}
